import React, { useCallback, useEffect, useState } from "react";
import { platformAPI } from "../services/api";
import Modal from "./Modal";

const number = (value) => Number(value).toLocaleString();

/** "Sales 12 · Credit payments 3": what a group holds, skipping the empty tables. */
const summary = (tables) => {
  const parts = tables.filter((t) => t.rows > 0).map((t) => `${t.table.replace(/_/g, " ")} ${number(t.rows)}`);
  return parts.length ? parts.join(" · ") : "nothing here";
};

// The two requests shops have actually made: a fresh start that keeps the catalogue
const PRESETS = [
  {
    label: "Everything except products",
    hint: "Products stay; their stock is set to zero.",
    except: ["products"],
  },
  {
    label: "Everything except products and stock",
    hint: "Products stay with their stock quantities and stock history.",
    except: ["products", "stock_counts", "stock_history"],
  },
];

/**
 * Clear part of a shop's data. Pick what to clear; "Review" runs the real
 * deletes inside a transaction that is rolled back and lists exactly what would
 * go; clearing needs the shop's slug typed and refuses if the data moved since
 * the review. See backend/src/services/shopDataService.js.
 */
export default function ShopDataTab({ shop }) {
  const [overview, setOverview] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const [typed, setTyped] = useState("");
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await platformAPI.shopData(shop.id);
      setOverview(data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not load this shop's data");
    }
  }, [shop.id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!overview) return error ? <p className="text-red-600">{error}</p> : <p className="text-gray-500">Loading…</p>;

  const toggle = (key) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const applyPreset = (preset) =>
    setPicked(new Set(overview.groups.filter((g) => !preset.except.includes(g.key) && g.total > 0).map((g) => g.key)));

  const askReview = async () => {
    setBusy(true);
    setError("");
    setDone(null);
    try {
      const { data } = await platformAPI.previewShopData(shop.id, [...picked]);
      setReview(data);
      setTyped("");
    } catch (err) {
      setError(err.response?.data?.error || "Could not prepare the review");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await platformAPI.clearShopData(shop.id, {
        groups: review.groups.map((g) => g.key),
        confirm: typed,
        fingerprint: review.fingerprint,
      });
      setDone(data);
      setReview(null);
      setPicked(new Set());
      await load();
    } catch (err) {
      setReview(null);
      setError(err.response?.data?.error || "Nothing was deleted: it did not work");
    } finally {
      setBusy(false);
    }
  };

  const nothingToClear = overview.groups.every((g) => g.total === 0);

  return (
    <div className="max-w-3xl">
      <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
        <p className="font-semibold">This permanently deletes data from {overview.shop.name}.</p>
        <p className="mt-1">
          It cannot be undone. Nothing is deleted until you have reviewed the exact list and typed the shop's slug.
          Never touched: {overview.kept.join(", ").toLowerCase()}.
        </p>
      </div>

      {overview.sales?.first_sale_at && (
        <p className="mb-4 text-sm text-gray-600">
          Sales from {new Date(overview.sales.first_sale_at).toLocaleDateString()} to{" "}
          {new Date(overview.sales.last_sale_at).toLocaleDateString()}.
        </p>
      )}

      {done && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
          Done: {number(done.total)} rows deleted or reset ({done.groups.map((g) => g.label).join(", ")}). Ask the
          owner to reload the app, and to check no till holds a queued sale.
        </div>
      )}
      {error && <p className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-500">Quick choice:</span>
        {PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            title={preset.hint}
            onClick={() => applyPreset(preset)}
            className="rounded-full bg-white px-3 py-1.5 font-medium text-gray-700 shadow-sm hover:bg-gray-100"
          >
            {preset.label}
          </button>
        ))}
        <button type="button" onClick={() => setPicked(new Set())} className="px-2 text-gray-500 hover:underline">
          Clear selection
        </button>
      </div>

      <ul className="space-y-2">
        {overview.groups.map((group) => {
          const empty = group.total === 0;
          return (
            <li key={group.key} className={`rounded-xl bg-white p-4 shadow ${empty ? "opacity-60" : ""}`}>
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5"
                  checked={picked.has(group.key)}
                  disabled={empty}
                  onChange={() => toggle(group.key)}
                />
                <span className="min-w-0">
                  <span className="block font-semibold text-gray-900">{group.label}</span>
                  <span className="block text-sm text-gray-600">{group.description}</span>
                  <span className="mt-1 block text-sm font-medium text-gray-800">{summary(group.tables)}</span>
                  {group.facts && (
                    <span className="block text-sm text-gray-500">
                      {number(group.facts.shelf_units)} units on the shelf, {number(group.facts.warehouse_units)} in
                      the warehouse
                    </span>
                  )}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        onClick={askReview}
        disabled={busy || picked.size === 0 || nothingToClear}
        className="mt-5 rounded-lg bg-red-600 px-5 py-2.5 font-semibold text-white hover:bg-red-700 disabled:opacity-40"
      >
        {busy ? "Preparing…" : `Review what will be deleted (${picked.size})`}
      </button>

      {review && (
        <Modal title={`Delete from ${overview.shop.name}?`} onClose={() => setReview(null)}>
          <p className="mb-3 text-sm text-gray-700">
            Dry run complete: this is exactly what will go. <strong>{number(review.total)}</strong> rows in total.
          </p>

          <ul className="mb-4 space-y-3">
            {review.groups.map((group) => (
              <li key={group.key} className="rounded-lg border border-gray-200 p-3">
                <p className="font-semibold text-gray-900">{group.label}</p>
                {group.added && (
                  <p className="mb-1 text-xs font-medium text-amber-700">
                    Added automatically: {group.added.why} (needed by “{group.added.because}”)
                  </p>
                )}
                <ul className="text-sm text-gray-700">
                  {group.tables.map((t) => (
                    <li key={t.table} className="flex justify-between gap-3">
                      <span>{t.table.replace(/_/g, " ")}</span>
                      <span className={t.rows ? "font-medium text-red-700" : "text-gray-400"}>
                        {number(t.rows)} {t.verb === "reset" ? "reset to zero" : "deleted"}
                      </span>
                    </li>
                  ))}
                </ul>
                {group.facts && (
                  <p className="mt-1 text-xs text-gray-500">
                    {number(group.facts.shelf_units)} shelf + {number(group.facts.warehouse_units)} warehouse units go to 0.
                  </p>
                )}
                {group.notes.map((note) => (
                  <p key={note} className="mt-1 text-xs text-amber-800">⚠ {note}</p>
                ))}
              </li>
            ))}
          </ul>

          <p className="mb-3 text-xs text-gray-500">Kept: {review.kept.join("; ").toLowerCase()}. Uploaded pictures stay on disk.</p>

          <label className="block text-sm text-gray-700">
            Type <code className="rounded bg-gray-100 px-1">{overview.shop.slug}</code> to confirm
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </label>

          <div className="mt-4 flex justify-end gap-3">
            <button type="button" onClick={() => setReview(null)} className="px-4 py-2 text-gray-600 hover:underline">
              Cancel
            </button>
            <button
              type="button"
              onClick={clear}
              disabled={busy || typed.trim().toLowerCase() !== overview.shop.slug}
              className="rounded-lg bg-red-600 px-5 py-2 font-semibold text-white hover:bg-red-700 disabled:opacity-40"
            >
              {busy ? "Deleting…" : "Delete permanently"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
